import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AdminEmailDialog from "@/components/AdminEmailDialog";
import { Trash2, RefreshCw, Search, Phone, Mail, X, Save, Download, Send, Copy, Loader2, CheckCircle2 } from "lucide-react";

const STATUS = [
  { value: "nouveau",     label: "Nouveau",       cls: "bg-rose-100 text-rose-800 border border-rose-200" },
  { value: "a_contacter", label: "À contacter",   cls: "bg-orange-100 text-orange-800 border border-orange-200" },
  { value: "contactee",   label: "Contactée",     cls: "bg-blue-100 text-blue-800 border border-blue-200" },
  { value: "en_cours",    label: "En cours",      cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  { value: "convertie",   label: "Convertie",     cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  { value: "sans_suite",  label: "Sans suite",    cls: "bg-slate-100 text-slate-700 border border-slate-200" },
];
const labelOf = (v) => STATUS.find((x)=>x.value===v)?.label || v;
const clsOf = (v) => STATUS.find((x)=>x.value===v)?.cls || "bg-slate-100";

function downloadCsv(url, filename) {
  api.get(url, { responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  });
}

export default function AdminPartnerSignups() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = () => api.get(`/admin/partner-signups${status?`?status=${status}`:""}`).then((r)=>setRows(r.data));
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [status]);

  const patch = async (id, updates) => { await api.patch(`/admin/partner-signups/${id}`, updates); load(); };
  const del = async (id) => {
    if (!confirm("Supprimer cette demande d'inscription ?")) return;
    await api.delete(`/admin/partner-signups/${id}`);
    toast.success("Supprimée"); setSelected(null); load();
  };

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return rows;
    return rows.filter((r) => [r.business_name, r.contact_name, r.email, r.phone, r.city, r.chosen_plan]
      .some((v) => v && String(v).toLowerCase().includes(s)));
  }, [rows, q]);

  const counts = useMemo(() => rows.reduce((acc, r) => {
    acc[r.status || "nouveau"] = (acc[r.status || "nouveau"] || 0) + 1; return acc;
  }, {}), [rows]);

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="admin-signups-title">Demandes d'inscription partenaire</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span className="text-rose-700 font-semibold">{counts.nouveau || 0} nouvelle(s)</span>
            <span className="text-blue-700 font-semibold">{counts.contactee || 0} contactée(s)</span>
            <span className="text-amber-700 font-semibold">{counts.en_cours || 0} en cours</span>
            <span className="text-emerald-700 font-semibold">{counts.convertie || 0} convertie(s)</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input placeholder="Entreprise, email, ville…" value={q} onChange={(e)=>setQ(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="signups-search"/>
          </div>
          <Select value={status || "all"} onValueChange={(v)=>setStatus(v==="all"?"":v)}>
            <SelectTrigger className="w-40" data-testid="signups-status-filter"><SelectValue placeholder="Statut"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {STATUS.map((s)=>(<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="signups-refresh"><RefreshCw size={14}/></Button>
          <Button variant="outline" onClick={()=>downloadCsv(`/admin/partner-signups-export`, "partenaires-inscriptions.csv")} data-testid="signups-export"><Download size={14} className="mr-1"/> Export</Button>
        </div>
      </div>

      <div className="mt-6 hidden md:block bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Entreprise</th>
              <th className="p-3 text-left">Contact</th>
              <th className="p-3 text-left">Ville</th>
              <th className="p-3 text-left">Formule</th>
              <th className="p-3 text-left">Statut</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${r.status==="nouveau"?"bg-rose-50/40":""}`}
                  onClick={()=>setSelected(r)} data-testid={`signup-row-${r.id}`}>
                <td className="p-3 whitespace-nowrap text-xs">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 font-semibold">{r.business_name}</td>
                <td className="p-3 text-xs">
                  <div>{r.contact_name}</div>
                  <div className="text-slate-500">{r.email}</div>
                </td>
                <td className="p-3">{r.city || "—"}</td>
                <td className="p-3"><span className="text-xs font-mono px-2 py-0.5 bg-slate-100 rounded">{r.chosen_plan}</span></td>
                <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${clsOf(r.status)}`}>{labelOf(r.status)}</span></td>
                <td className="p-3 text-right">
                  <button onClick={(e)=>{ e.stopPropagation(); del(r.id); }} className="text-rose-600 hover:text-rose-800"><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500">Aucune demande.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-4 md:hidden space-y-2">
        {filtered.map((r) => (
          <button key={r.id} onClick={()=>setSelected(r)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold truncate">{r.business_name}</div>
                <div className="text-xs text-slate-500 truncate">{r.email} · {r.city || "—"}</div>
              </div>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${clsOf(r.status)}`}>{labelOf(r.status)}</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">Formule : <b>{r.chosen_plan}</b></div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-center text-slate-500 p-6">Aucune demande.</div>}
      </div>

      {selected && <SignupDrawer signup={selected} onClose={()=>setSelected(null)}
                                  onPatch={async (u)=>{ await patch(selected.id, u); setSelected({ ...selected, ...u }); toast.success("Enregistré"); }}
                                  onDelete={()=>del(selected.id)}
                                  onReload={load}/>}
    </div>
  );
}


function SignupDrawer({ signup, onClose, onPatch, onDelete, onReload }) {
  const [form, setForm] = useState({
    status: signup.status || "nouveau",
    priority: signup.priority || "normale",
    assigned_to: signup.assigned_to || "",
    internal_notes: signup.internal_notes || "",
  });
  const BILLABLE = ["fondateur", "essentiel", "visibilite", "premium"];
  const [plan, setPlan] = useState(BILLABLE.includes(signup.chosen_plan) ? signup.chosen_plan : "fondateur");
  const [sending, setSending] = useState(false);
  const [approving, setApproving] = useState(false);
  const [lastUrl, setLastUrl] = useState("");
  const dirty = ["status","priority","assigned_to","internal_notes"].some((k)=>form[k] !== (signup[k]||""));
  const save = () => onPatch(form);
  const set = (k) => (e) => setForm((s)=>({...s, [k]: e.target?.value ?? e}));

  const sendLink = async () => {
    if (!confirm(`Envoyer un lien de paiement Stripe pour la formule ${plan.toUpperCase()} à ${signup.email} ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/partner-signups/${signup.id}/send-checkout-link`, { plan_key: plan });
      setLastUrl(data.checkout_url || "");
      toast.success("Lien de paiement envoyé par email au partenaire");
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi du lien");
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    if (!lastUrl) return;
    try { await navigator.clipboard.writeText(lastUrl); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const approve = async () => {
    if (!confirm(`Accepter la demande et créer l'espace pro de « ${signup.business_name} » ? Ses identifiants de connexion lui seront envoyés par email.`)) return;
    setApproving(true);
    try {
      const { data } = await api.post(`/admin/partner-signups/${signup.id}/approve`, { plan_key: signup.chosen_plan || "gratuit" });
      toast.success(data.already_existed
        ? "Un compte existait déjà pour cet email — demande marquée convertie."
        : "Espace pro créé ✓ Identifiants envoyés par email au partenaire.");
      onReload?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'acceptation");
    } finally { setApproving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex md:justify-end p-0 md:p-4" onClick={onClose}>
      <div className="bg-white w-full md:max-w-lg md:rounded-2xl md:my-auto md:max-h-[92vh] overflow-y-auto shadow-2xl"
           onClick={(e)=>e.stopPropagation()} data-testid="signup-drawer">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-start justify-between z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#E05638] font-bold">Demande partenaire</div>
            <div className="font-heading text-xl font-bold truncate">{signup.business_name}</div>
            <div className="text-xs text-slate-500">{new Date(signup.created_at).toLocaleString("fr-FR")}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={18}/></button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <a href={signup.phone ? `tel:${signup.phone}` : undefined}
               className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold ${signup.phone ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-100 text-slate-400 pointer-events-none"}`}>
              <Phone size={14}/> Appeler
            </a>
            <AdminEmailDialog recipients={[signup.email]} defaultSubject="Votre demande PROXIreduc-28"
              buttonClassName="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
              buttonLabel={<><Mail size={14}/> Email</>} testId="signup-mail" />
          </div>

          <div className="text-sm space-y-1">
            <div><span className="font-semibold">Contact :</span> {signup.contact_name}</div>
            <div><span className="font-semibold">Email :</span> {signup.email}</div>
            <div><span className="font-semibold">Téléphone :</span> {signup.phone || "—"}</div>
            <div><span className="font-semibold">Adresse :</span> {[signup.address, signup.postal_code, signup.city].filter(Boolean).join(", ") || "—"}</div>
            <div><span className="font-semibold">Catégorie :</span> {signup.category || "—"}</div>
            <div><span className="font-semibold">Formule demandée :</span> <b>{signup.chosen_plan}</b></div>
            {signup.website && <div><span className="font-semibold">Site :</span> <a href={signup.website} target="_blank" rel="noreferrer" className="text-[#E05638] underline">{signup.website}</a></div>}
          </div>

          {signup.offer_proposed && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
              <div className="text-[10px] uppercase tracking-widest text-orange-700 font-bold">Offre proposée</div>
              <div className="mt-1 text-slate-700">{signup.offer_proposed}</div>
            </div>
          )}

          {signup.message && (
            <div className="p-3 bg-slate-50 rounded-lg text-sm whitespace-pre-line">{signup.message}</div>
          )}

          {/* Accepter directement : crée le compte sans paiement */}
          <div className="p-4 rounded-xl border border-emerald-300 bg-emerald-50/60" data-testid="approve-block">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={15} className="text-emerald-600"/>
              <div className="font-semibold text-slate-900">Accepter la demande</div>
            </div>
            <p className="text-xs text-slate-600 mb-3">Crée immédiatement l'espace professionnel (formule <b>{signup.chosen_plan || "gratuit"}</b>) et envoie les identifiants de connexion par email, sans paiement.</p>
            <Button onClick={approve} disabled={approving}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-signup-btn">
              {approving ? <Loader2 size={14} className="animate-spin mr-1"/> : <CheckCircle2 size={14} className="mr-1"/>}
              {approving ? "Création…" : "Accepter & créer l'espace pro"}
            </Button>
          </div>

          {/* Bloc Envoyer lien Stripe */}
          <div className="p-4 rounded-xl border border-[#E05638]/30 bg-orange-50/50" data-testid="checkout-link-block">
            <div className="flex items-center gap-2 mb-2">
              <Send size={14} className="text-[#E05638]"/>
              <div className="font-semibold text-slate-900">Envoyer un lien de souscription</div>
            </div>
            <p className="text-xs text-slate-600 mb-3">Génère un lien Stripe personnalisé et l'envoie par email au futur partenaire, qui règle lui-même par carte. Le compte pro est créé automatiquement après paiement.</p>
            <div className="flex gap-2">
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="flex-1" data-testid="checkout-link-plan"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fondateur">PARTENAIRE FONDATEUR — 0 €/12 mois</SelectItem>
                  <SelectItem value="essentiel">PROXI ESSENTIEL — 9,90 €/mois</SelectItem>
                  <SelectItem value="visibilite">PROXI VISIBILITÉ — 19,90 €/mois</SelectItem>
                  <SelectItem value="premium">PROXI PREMIUM — 29,90 €/mois</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={sendLink} disabled={sending}
                      className="btn-shine text-white" data-testid="send-checkout-link-btn">
                {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14} className="mr-1"/>}
                {sending ? "…" : "Envoyer"}
              </Button>
            </div>
            {signup.checkout_link_sent_at && (
              <div className="mt-2 text-[11px] text-emerald-700">
                ✓ Lien envoyé le {new Date(signup.checkout_link_sent_at).toLocaleString("fr-FR")}
              </div>
            )}
            {lastUrl && (
              <div className="mt-2 flex gap-2 items-center">
                <input readOnly value={lastUrl} className="flex-1 text-[10px] font-mono px-2 py-1 rounded bg-white border border-slate-200 truncate"/>
                <button onClick={copyLink} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Copier"><Copy size={14}/></button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v)=>setForm((s)=>({...s, status: v}))}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{STATUS.map((s)=>(<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Responsable</Label>
              <Input value={form.assigned_to} onChange={set("assigned_to")} placeholder="Initiales"/>
            </div>
          </div>
          <div>
            <Label>Notes internes</Label>
            <Textarea rows={4} value={form.internal_notes} onChange={set("internal_notes")}/>
          </div>

          <div className="flex gap-2">
            <Button disabled={!dirty} onClick={save} className="btn-shine text-white flex-1"><Save size={14} className="mr-1"/> Enregistrer</Button>
            <Button variant="outline" onClick={onDelete} className="text-rose-600 hover:text-rose-800"><Trash2 size={14}/></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
